'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import NextLink from 'next/link';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { buttonVariants } from '@/components/ui/button';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import { registeredEntryState } from '@/app/[locale]/event/[eventId]/libs/registeredEntryState';
import {
  useRegistrationStatus,
  type EntryMembership,
  type RegisteredGroup,
} from '../libs/useRegistrationStatus';
import { EventRegistrationCard } from './EventRegistrationCard';
import { RegisteredTeamEntry, type TeamFields } from './RegisteredTeamEntry';

/** A team-division entry always carries a roster (required at registration). */
const isTeamGroup = (g: RegisteredGroup) => g.team_info != null;
const isWithdrawn = (g: RegisteredGroup) => g.status === 'withdrawn';

interface EventRegistrationClientProps {
  event: {
    id: string;
    slug: string;
    registration_opens?: string | null;
    registration_closes?: string | null;
  };
  locale: string;
  canRegister: boolean;
  isRegistrationOpen: boolean;
}

export function EventRegistrationClient({
  event,
  locale,
  canRegister,
  isRegistrationOpen,
}: EventRegistrationClientProps) {
  const t = useTranslations('Event');
  // Shared with EventRegisterBottomBar — one session check + one status GET
  // per page view for both consumers.
  const { isAuthenticated, isRegistered, registeredGroups, canAddMore, currency, membership } =
    useRegistrationStatus(event.id);
  const money = (n: number) => formatCurrency(locale, currency, n);
  /** Status / payment state beside an entry (survey follow-ups 9 and 11). */
  const stateLabel = (g: RegisteredGroup): string => {
    const state = registeredEntryState(g);
    switch (state.kind) {
      case 'withdrawn':
        return t('registration.alreadyRegistered.entryWithdrawn');
      case 'none':
        return t('registration.alreadyRegistered.entryNoPayment');
      case 'refunded':
        return t('registration.alreadyRegistered.entryRefunded');
      case 'partlyRefunded':
        return t('registration.alreadyRegistered.entryPartlyRefunded');
      case 'partlyPaid':
        return t('registration.alreadyRegistered.entryPartlyPaid', {
          paid: money(state.paid),
          total: money(state.total),
        });
      case 'owing':
        return t('registration.alreadyRegistered.entryOwing', { amount: money(state.amount) });
      case 'free':
        return t('registration.alreadyRegistered.entryFree');
      case 'paid':
        return t('registration.alreadyRegistered.entryPaid');
    }
  };
  /**
   * The entered player's membership coverage line (Members phase 4b, §6.3): what the gate says about
   * their first day of the event, with Renew where renewing helps. Renew opens the club's offers
   * preselected on the offer's type and season; with no offer it opens the memberships list.
   */
  const coverage = (m: EntryMembership | null): { text: string; renew: boolean } | null => {
    if (!m) return null;
    const m_ = 'registration.alreadyRegistered.membership';
    const day = (iso: string | null | undefined) => (iso ? formatDate(iso, locale, 'UTC') : null);
    if (m.status === 'eligible') return { text: t(`${m_}.covered`), renew: false };
    switch (m.reason) {
      case 'membership_ends_before_event': {
        const d = day(m.ends_on);
        return { text: d ? t(`${m_}.endsBefore`, { date: d }) : t(`${m_}.lapsed`), renew: true };
      }
      case 'in_grace': {
        const d = day(m.grace_until);
        return { text: d ? t(`${m_}.inGrace`, { date: d }) : t(`${m_}.lapsed`), renew: true };
      }
      case 'membership_lapsed':
        return { text: t(`${m_}.lapsed`), renew: true };
      case 'renewal_scheduled': {
        const d = day(m.renews_on);
        return { text: d ? t(`${m_}.renews`, { date: d }) : t(`${m_}.renewsSoon`), renew: false };
      }
      default:
        return null;
    }
  };
  const renewHref = (m: EntryMembership): string =>
    m.renewal_offer
      ? `/${locale}/memberships/${m.renewal_offer.club_id}?type=${encodeURIComponent(m.renewal_offer.member_type_id)}&season=${encodeURIComponent(m.renewal_offer.valid_from)}`
      : `/${locale}/memberships`;

  // Team edits saved from this card, layered over the fetched status so the
  // list reflects the save without a refetch (the status hook is one-shot per
  // page view, shared with the mobile sticky bar).
  const [teamOverrides, setTeamOverrides] = useState<Record<string, TeamFields>>({});
  const groups = registeredGroups.map((g) =>
    teamOverrides[g.id] ? { ...g, ...teamOverrides[g.id] } : g,
  );

  // Already registered — show confirmation
  if (isAuthenticated && isRegistered) {
    const registerUrl = `/${locale}/event/${event.id}/${encodeURIComponent(event.slug ?? '')}/register`;
    // Every entry withdrawn by the organiser: not "Already Registered" any more.
    const allWithdrawn = groups.length > 0 && groups.every(isWithdrawn);
    const line = allWithdrawn ? null : coverage(membership);
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-green-200 bg-green-50 p-6 dark:border-green-800 dark:bg-green-900/20">
          <div className="text-center">
            {allWithdrawn ? (
              <>
                <div className="mb-1 text-lg font-semibold text-green-700 dark:text-green-300">
                  {t('registration.alreadyRegistered.withdrawnTitle')}
                </div>
                <p className="mb-3 text-sm text-green-700 dark:text-green-300">
                  {t('registration.alreadyRegistered.withdrawnMessage')}
                </p>
              </>
            ) : (
              <div className="mb-2 text-lg font-semibold text-green-600 dark:text-green-400">
                ✓ {t('registration.alreadyRegistered.title')}
              </div>
            )}
            {registeredGroups.length > 0 ? (
              <div className="mb-3">
                <p className="mb-1 text-xs font-medium tracking-wide text-green-700/70 uppercase dark:text-green-300/70">
                  {t('registration.alreadyRegistered.yourDivisions')}
                </p>
                <ul className="space-y-1">
                  {groups.map((g) =>
                    isTeamGroup(g) ? (
                      <RegisteredTeamEntry
                        key={g.id}
                        eventId={event.id}
                        group={g}
                        canEdit={isRegistrationOpen && !isWithdrawn(g)}
                        stateLabel={stateLabel(g)}
                        onSaved={(groupId, fields) =>
                          setTeamOverrides((prev) => ({ ...prev, [groupId]: fields }))
                        }
                      />
                    ) : (
                      <li
                        key={g.id}
                        className="text-sm font-medium text-green-700 dark:text-green-300"
                      >
                        {g.name}{' '}
                        <span className="text-xs font-normal opacity-80">{stateLabel(g)}</span>
                      </li>
                    ),
                  )}
                </ul>
                {!isRegistrationOpen && groups.some((g) => isTeamGroup(g) && !isWithdrawn(g)) && (
                  <p className="mt-2 text-xs text-green-700/70 dark:text-green-300/70">
                    {t('registration.teamEdit.locked')}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-green-700 dark:text-green-300">
                {t('registration.alreadyRegistered.message')}
              </p>
            )}
            {line && membership && (
              <div
                data-testid="entry-membership-line"
                className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm text-green-700 dark:text-green-300"
              >
                <span>{line.text}</span>
                {line.renew && (
                  <NextLink
                    href={renewHref(membership)}
                    className="font-semibold underline underline-offset-2"
                  >
                    {t('registration.alreadyRegistered.membership.renew')}
                  </NextLink>
                )}
              </div>
            )}
            {canAddMore && isRegistrationOpen && (
              <NextLink
                href={registerUrl}
                className={cn(
                  buttonVariants.base,
                  buttonVariants.variants.variant.primary,
                  buttonVariants.variants.size.lg,
                  // Same treatment as the register CTA (EventRegistrationCard):
                  // primary, full-width, arrow — and desktop-only, because on
                  // mobile the sticky bottom bar carries this CTA.
                  'mt-3 hidden h-12 min-h-[3rem] w-full font-semibold transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] md:inline-flex',
                )}
              >
                {t('registration.alreadyRegistered.addDivision')}
                <ArrowRightIcon className="ml-2 h-4 w-4 shrink-0" />
              </NextLink>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Register button — visible for unauthenticated and authenticated-not-registered users
  // The register page handles auth flow itself
  return (
    <EventRegistrationCard
      event={event}
      canRegister={canRegister}
      isRegistrationOpen={isRegistrationOpen}
    />
  );
}
